import { useEffect, useState } from 'react';
import { Checkbox, Divider, FormControlLabel, Link, Stack, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useLocation, useNavigate } from 'react-router-dom';

import TextInput from 'components/Common/TextInput';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import CustomButton from 'components/Common/CustomButton';
import InfoModal from 'components/Common/InfoModal';
import ReceiptModalCard from 'components/Common/ReceiptModalCard';
import CardWrapper from 'components/Common/CardWrapper';
import BankCardAutocomplete from 'components/Common/autocomplete/BankCardAutoComplete';
import Routes from 'constants/routes';
import { notify } from 'components/Common/Toast';
import { SendRequestPayload, requestMoney } from 'redux/slices/sendAndRequest/requestMoneyActivitySlice';
import { camelToSpacedPascalCase, formatAmount, getFullName, parseToFloatString } from 'utils/helpers';
import { MERCHANT_TYPE, PERSONAL_TYPE, SEND_MONEY, SEND_REQUEST } from 'types/sendAndRequest';
import { sendMoney, SendMoneyPayload } from 'redux/slices/sendAndRequest/sendMoneyActivitySlice';
import DailyLimitModal from './DailyLimitModal';
import { fetchDailyLimits } from 'redux/slices/user/dailyLimitSlice';
interface SendMoneyFormInputs {
    to: string;
    amount: string;
    from: number | null;
    note: string;
    isBusiness?: boolean;
}
interface SendAndRequestMoneyFormType {
    title: string;
    modelTitle?: string;
}

export default function SendAndRequestMoneyForm({ title }: SendAndRequestMoneyFormType) {
    const navigation = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [loading, setLoading] = useState<boolean>(false);
    const moneyContainerList = useSelector((state: RootState) => state.moneyContainer?.moneyContainerList) || [];
    const userInfo = useSelector((state: RootState) => state.user.userInfo);
    const [limitModal, setLimitModal] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const { accountBalance, nickname: username } = userInfo || {};
    const location = useLocation();
    const { nickname, receiverType, imgUrl, firstName, lastName, name } = location.state || '';
    const fullName = name || getFullName({ firstName, lastName });
    const currentDate = new Date();
    const { dailyM2pRemainingSendLimit, dailyM2mRemainingSendLimit } = useSelector(
        (state: RootState) => state?.dailyLimit?.dailyLimits || {}
    );
    const dailyLimitLoading = useSelector((state: RootState) => state?.dailyLimit?.loading);
    const remainingLimit = receiverType === 'personal' ? dailyM2pRemainingSendLimit : dailyM2mRemainingSendLimit;
    const isLimitExceed = !remainingLimit;

    const formSchema = yup.object({
        amount: yup
            .string()
            .required('Amount is required.')
            .test('amount', `Amount must be greater than $0.00 `, (value: any) => !(Number(value) < 0.01))
            .test('amount', `Amount must be less than $${accountBalance} `, (value: any) => !(Number(value) < 0.01)),
        from: yup.string().required('From is required.'),
        note: yup.string(),
    });

    const {
        handleSubmit,
        getValues,
        formState: { errors },
        control,
        watch,
        setValue,
        trigger,
    } = useForm<SendMoneyFormInputs>({
        resolver: yupResolver<any>(formSchema),
        defaultValues: {
            isBusiness: false,
            to: nickname,
            amount: '0',
            from: moneyContainerList?.[0]?.detail?.containerId || null,
            note: '',
        },
    });

    useEffect(() => {
        dispatch(fetchDailyLimits());
    }, [dispatch]);

    const handleFormSubmit = () => {
        setModalOpen(true);
    };

    const handleConfirm = async () => {
        const formValues = getValues();
        const { amount: _amount, note, from, to, isBusiness } = formValues;
        const amount = +parseToFloatString(_amount);
        try {
            setLoading(true);
            if (title === SEND_REQUEST) {
                if (to === username) {
                    return notify(`You can't request money to yourself.`, 'error');
                }
                const payload: SendRequestPayload = {
                    nickname: to,
                    amount,
                    subject: note,
                    toSva: from, // Indicates if the user wants to request the money to a specific container 33409
                };
                const res = await dispatch(requestMoney({ payload }));
                const { code, transactionId } = res?.payload || {};
                if (code === 200 && !!transactionId) {
                    notify('Request sent successfully.', 'success');
                    navigation(Routes.sendAndRequest.requestMoneyTransactionDetails.replace(':transactionId', transactionId.toString()));
                }
                return;
            }
            const formData: SendMoneyPayload = {
                amount,
                subject: note,
                nickname: to,
                fromSva: from,
                senderIsBusiness: true,
                receiverIsBusiness: true,
            };
            if (receiverType === PERSONAL_TYPE) {
                if (!isBusiness) {
                    formData.senderIsBusiness = false;
                    formData.receiverIsBusiness = false;
                }
            }
            if (to === username) {
                return notify(`You can't send money to yourself.`, 'error');
            }
            const response = await dispatch(sendMoney({ payload: formData, type: receiverType }));
            const { code, transactionId } = response?.payload || {};
            const filterTrxData = {
                title: 'To',
                transactionAmount: formatAmount(amount, 'dollar', 'TRANSFER'),
                note,
                trxId: transactionId,
                transactionDateTime: currentDate,
                name: fullName,
                username: to,
                logoUrl: imgUrl,
                status: 'COMPLETED',
                type: 'Payment sent',
            };
            if (code === 200) {
                notify('Money sent successfully.', 'success');
                const path = Routes.transactions.transactionDetails;
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                transactionId &&
                    navigation(path.replace(':transactionId', transactionId?.toString()), {
                        state: { sendMoneyForm: true, filterTrxData },
                    });
            }
        } catch (error) {
            console.error('Error making payment:', error);
        } finally {
            setLoading(false);
            setModalOpen(false);
        }
    };
    return (
        <>
            <CardWrapper>
                <Typography sx={{ fontSize: 16, mb: 2, fontWeight: 400 }} variant="h1">
                    {camelToSpacedPascalCase(title)}
                </Typography>
                <Divider />
                <Typography sx={{ mt: 2, mb: 4 }} variant="body1">
                    Fill in the necessary information and optional note then click send to deliver this payment to your chosen recipient.
                </Typography>
                <form onSubmit={handleSubmit(handleFormSubmit)}>
                    <Stack alignItems="center" gap={2}>
                        <TextInput disabled value={watch('to')} label="To" icon={null} sx={{ width: { xs: '100%', md: '50%' } }} />
                        <Controller
                            name="from"
                            control={control}
                            render={({ field: { ...rest } }) => (
                                <BankCardAutocomplete
                                    {...rest}
                                    sx={{ width: { xs: '100%', md: '50%' } }}
                                    label="Account"
                                    value={watch('from') || null}
                                    matchKey="detail.containerId"
                                    isOptionEqualToValue={(option) => {
                                        const value = option?.detail?.containerId === watch('from');
                                        return value;
                                    }}
                                    onChange={(_, option) => {
                                        setValue('from', option?.detail?.containerId);
                                        trigger('from');
                                    }}
                                    error={!!errors.from}
                                    helperText={(errors?.from as any)?.message}
                                />
                            )}
                        />

                        <Controller
                            name="amount"
                            control={control}
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    type={'currency'}
                                    sx={{ width: { xs: '100%', md: '50%' } }}
                                    label="Amount"
                                    placeholder="$0.00"
                                    icon={null}
                                    error={!!errors.amount}
                                    helperText={(errors?.amount as any)?.message}
                                    // onChange={(e) => {
                                    //     const value = parseFloat(e.target.value);
                                    //     onChange(value.toFixed(2));
                                    // }}
                                />
                            )}
                        />
                        <Stack width={{ xs: '100%', md: '50%' }} sx={{ cursor: 'pointer' }} alignItems={'end'}>
                            <Link onClick={() => setLimitModal(true)}>View Limits</Link>
                        </Stack>
                        <Controller
                            name="note"
                            control={control}
                            render={({ field }) => (
                                <TextInput
                                    {...field}
                                    label="Optional note"
                                    icon={null}
                                    sx={{ mb: 18, p: 0, width: { xs: '100%', md: '50%' } }}
                                    multiline
                                    minRows={6}
                                    maxRows={6}
                                    adornmentMargin={-15}
                                />
                            )}
                        />
                        {!dailyLimitLoading && isLimitExceed && (
                            <Typography color="error" sx={{ mt: 2 }}>
                                Your daily sending limit is exceeded.
                            </Typography>
                        )}
                        <CustomButton
                            loading={dailyLimitLoading}
                            type="submit"
                            disabled={isLimitExceed || dailyLimitLoading}
                            sx={{ fontSize: '26px', width: { xs: '90%', md: '40%' } }}
                        >
                            {camelToSpacedPascalCase(title)}
                        </CustomButton>
                    </Stack>
                    <DailyLimitModal
                        isReceiverBusiness={receiverType === MERCHANT_TYPE}
                        limitModal={limitModal}
                        setLimitModal={setLimitModal}
                        loading={dailyLimitLoading}
                    />
                    {/* Confirm Invoice Modal */}
                    <InfoModal
                        loading={loading}
                        description={`Please confirm all the details below are correct, once you press confirm you cannot edit this ${title === SEND_MONEY ? 'transaction' : 'request'}.`}
                        isOpen={modalOpen}
                        onClose={() => setModalOpen(false)}
                        title={title === SEND_MONEY ? 'Confirm Send' : 'Confirm Request'}
                        onConfirm={handleConfirm}
                    >
                        {
                            <>
                                <ReceiptModalCard to={watch('to')} amount={watch('amount')} note={watch('note') || ''} />
                                {receiverType === PERSONAL_TYPE && (
                                    <Stack>
                                        <Controller
                                            name="isBusiness"
                                            control={control}
                                            render={({ field }) => (
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            {...field}
                                                            checked={field.value}
                                                            sx={{
                                                                color: '#d3d3d3',
                                                                '&.Mui-checked': {
                                                                    color: '#142456',
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    label="Business transaction?"
                                                />
                                            )}
                                        />
                                    </Stack>
                                )}
                            </>
                        }
                    </InfoModal>
                </form>
            </CardWrapper>
        </>
    );
}
